import {
  workFlowTasks,
  workFlowTasksMiscData,
  workFlowTasksJsonV3,
} from '../constants/WorkFlowConstants';

import {
  createDatabaseTable,
  addDatabaseItem,
  getCustomDatabaseItems,
} from '@/services/axios/data.api';

import { getPreSignedUrl, uploadFile, getFileExtension } from '@/utils/helpers.js';

import store from '../../store/store';

import fontsDatabaseSchema from '@/assets/data-schemas/fontsDatabaseSchema.json';

export const generateTaskObject = ({
  id,
  name,
  caption,
  props,
  customIcon,
}) => {
  const task = {
    id,
    name,
    caption,
    props,
    input: [],
    output: [],
    shape: { x: 0, y: 0 },
    previousTaskId: null,
  };
  if (customIcon) {
    task['customIcon'] = customIcon;
  }
  return task;
};

export const generateTaskMiskDataObject = ({
  isDroppable,
  isDeletable,
  isActive,
  icon,
  appearance,
  description,
  dropzones,
  options,
}) => {
  return {
    isDroppable,
    isDeletable,
    isActive,
    style: {
      icon,
      width: 90,
      height: 90,
      appearance,
    },
    description,
    dropzones,
    options,
  };
};

export const getValidatedTasksPosition = (taskX, taskY, taskName) => {
  const container = { width: 6000, height: 6000 };
  let width = 90,
    height = 90;

  if (workFlowTasksMiscData[taskName]) {
    width = workFlowTasksMiscData[taskName].style.width;
    height = workFlowTasksMiscData[taskName].style.width;
  }

  if (taskX > container.width) {
    taskX = Math.max(0, container.width - width);
    if (taskY + height < container.height) taskY += height;
  }

  if (taskY > container.height) {
    taskY = Math.max(0, container.height - height);
  }

  if (taskY < 0) {
    taskY = 50;
  }

  if (taskX < 0) {
    taskX = 0;
  }

  return { x: taskX, y: taskY };
};

export const getDefaultTaskName = (existingTaskNames, defaultTaskName) => {
  if (!existingTaskNames.includes(defaultTaskName)) {
    return defaultTaskName;
  }

  const numbers = existingTaskNames
    .filter((name) => name.startsWith(defaultTaskName + '_'))
    .map((name) => {
      const match = name.match(new RegExp(`${defaultTaskName}_(\\d+)$`));
      return match ? parseInt(match[1]) : 0;
    });

  const maxNumber = numbers.reduce((max, num) => Math.max(max, num), 0);

  return `${defaultTaskName}_${maxNumber + 1}`;
};

export const getWorkflowDatabaseTaskSchema = (startTask, params) => {
  const databaseScheme = workFlowTasks.find((ele) => ele.name === 'Database');
  const propItems = params.navbarElements.reduce((acc, ele) => {
    acc[ele.name] = `{${ele.name}}`;

    return acc;
  }, {});
  databaseScheme.id = startTask.id + 1;
  databaseScheme.previousTaskId = startTask.id;
  databaseScheme.props = {
    database: params.database.name,
    databaseName: 'Database_Antwort',
    item: propItems,
  };
  databaseScheme.shape.x = startTask.shape.x + 150;
  databaseScheme.shape.y = startTask.shape.y - 150;

  return databaseScheme;
};

export const getEndTaskList = () => {
  return ['Redirect', 'JsonMapper', 'End'];
};

export const getWorkflowTasks = (flowVersion = 3) => {
  return flowVersion < 3 ? workFlowTasks : workFlowTasksJsonV3;
};

export function generateStructureObject(
  { label = 'Tab 1', elementId = 0 },
  identifier = 'tab'
) {
  const struct = {
    col: () => ({ elementId: elementId }),
    row: () => ({
      cols: [],
    }),
    tab: () => ({
      label: label,
      rows: [],
    }),
  };

  return struct[identifier]?.call();
}

export function checkUrlPermission(whitelistedUrls = []) {
  if (whitelistedUrls.length == 0) return true;

  // Get the current request origin (browser's origin)
  let requestOrigin = window.location.origin;
  let isWhitelistUrl = false;

  // Check if flow is allowed to run from this origin
  requestOrigin = getCleanUrl(requestOrigin);

  for (let url of whitelistedUrls) {
    url = getCleanUrl(url);

    // Handle wildcards
    if (url.includes('*') && requestOrigin.endsWith(url.replace('*', ''))) {
      isWhitelistUrl = true;
      break;
    } else if (url === requestOrigin) {
      isWhitelistUrl = true;
      break;
    }
  }

  if (!isWhitelistUrl) {
    throw new Error(`Not Authorized: ${requestOrigin}`);
  }

  return true;
}

export function getCleanUrl(url) {
  if (url) {
    // Remove https:// and www from the base url
    url = url.replace('https://', '').replace('www.', '');

    // Remove only the last trailing slash
    if (url[url.length - 1] === '/') {
      url = url.slice(0, -1);
    }
  }
  return url;
}
export const FONTS_DATABASE_TYPE = 'custom-fonts';

export const getCustomFontsDatabase = () => {
  return store.getters.getDatabaseList?.find(
    (database) => database.type === FONTS_DATABASE_TYPE
  );
};
export const createFontsDatabase = async () => {
  try {
    const databaseSchema = fontsDatabaseSchema.map((schema, index) => ({
      ...schema,
      id: Date.now() + (index + 1),
    }));

    const formData = new FormData();
    formData.append('caption', 'system - custom-fonts');
    formData.append('add_only', false);
    formData.append('icon', null);
    formData.append(
      'tablename',
      `${store.getters?.getUser?.username}_system-custom-fonts`
    );

    formData.append('tabletype', FONTS_DATABASE_TYPE);
    formData.append('type', FONTS_DATABASE_TYPE);
    formData.append('database_type', 'DynamoDB');
    formData.append('schema', JSON.stringify(databaseSchema));
    formData.append(
      'layout',
      '{"tabs":[{"id":0, "rows":[{"id":0, "cols":[{"id":0}]}]}]}'
    );

    await createDatabaseTable(formData);
  } catch (error) {
    throw new Error('error while creating database table');
  }
};

export const saveCustomFonts = async (item) => {
  try {
    let custonFontsDatabase = getCustomFontsDatabase();
    if (!custonFontsDatabase) {
      await createFontsDatabase();
      await store.dispatch('fetchUserDataBase');
      custonFontsDatabase = getCustomFontsDatabase();
    }

    const preSignedUrl = await getPreSignedUrl(
      custonFontsDatabase?.name,
      item.font.name
    );
    // formBuilderItem.value.link = preSignedUrl.signature.key;
    await uploadFile(preSignedUrl, item.font);

    const payload = {
      item: {
        ...item,
        font: {
          extension: getFileExtension(item.font.name),
          size: item.font.size / 1000,
          created: item.font.lastModified,
          name: item.font.name,
          link: preSignedUrl.signature.key,
          modified: item.font.lastModified,
          readOnly: true,
          type: item.font.type,
        },
      },
    };

    return await addDatabaseItem(custonFontsDatabase?.name, payload);
  } catch (error) {
    throw new Error('error while creating database item');
  }
};

export const getCustomFonts = async () => {
  try {
    const database = getCustomFontsDatabase();

    if (database) {
      const response = await getCustomDatabaseItems(database.name, '', true);

      const data = response.data.items;
      return data;
    }
    return [];
  } catch (error) {
    return [];
  }
};

export const extractInputValuesFromCode = (codeString) => {
  const result = {};
  const regex = /input\["([^"]+)"\]/g;
  let match;

  while ((match = regex.exec(codeString)) !== null) {
    const key = match[1];

    const assignRegex = new RegExp(`input\\["${key}"\\]\\s*=\\s*(.+)`);
    const assignMatch = codeString.match(assignRegex);

    if (assignMatch) {
      const rawValue = assignMatch[1].trim();

      let parsedValue;
      let type;

      try {
        if (/^\d+$/.test(rawValue)) {
          parsedValue = Number(rawValue);
          type = 'number';
        } else if (/^\[.*\]$/.test(rawValue)) {
          parsedValue = eval(rawValue);
          type = 'array';
        } else if (/^\{.*\}$/.test(rawValue)) {
          parsedValue = eval('(' + rawValue + ')');
          type = 'object';
        } else if (/^".*"$/s.test(rawValue)) {
          parsedValue = rawValue.slice(1, -1);
          type = 'text';
        } else {
          parsedValue = rawValue;
          type = 'text';
        }
      } catch (e) {
        parsedValue = rawValue;
        type = 'text';
      }

      result[key] = { value: parsedValue, type };
    } else {
      result[key] = { value: '', type: 'text' };
    }
  }

  return result;
};
